import {
  type AdminClient,
  AdminApiError,
  type TimingOverride,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { timingSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';

export interface TimingPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
  /** Picker on "Provider · all integrators". Rounds belong to real tenants, not
   *  the provider row, so the live readout explains the empty state instead of
   *  reporting a misleading "idle". */
  isProviderScope?: boolean;
}

interface ActiveRoundView {
  id: string;
  state: string;
  bettingClosesAt: number;
  endedAt?: number;
}

/** How often the live readout re-reads round state from the engine. */
const ROUND_POLL_MS = 1_000;
/** Countdown repaint cadence (local clock only — no network). */
const ROUND_TICK_MS = 100;

/**
 * Deadline of the round's CURRENT phase.
 *
 * Mirrors the engine's `computeNextState` ladder (round-engine
 * `src/orchestrator.ts`): every boundary is `bettingClosesAt` plus the running
 * sum of the configured durations. The round row persists only startedAt /
 * bettingClosesAt / endedAt — there is no per-phase deadline to read — so the
 * readout derives it from the same two inputs the engine ticks against.
 *
 * Returns null for phases with no clock-driven deadline: SETTLING ends when
 * settle completes (wallet-bound, not timer-bound), and idle/finished have
 * nothing to count down to.
 */
export function phaseDeadline(
  round: Pick<ActiveRoundView, 'state' | 'bettingClosesAt'>,
  t: TimingOverride,
): number | null {
  const bettingEnd = round.bettingClosesAt;
  const closingEnd = bettingEnd + t.bettingClosingMs;
  const playingEnd = closingEnd + t.playingMs;
  const revealEnd = playingEnd + t.revealMs;
  const showingEnd = revealEnd + t.showingResultMs;
  switch (round.state) {
    case 'betting_open':
      return bettingEnd;
    case 'betting_closing':
      return closingEnd;
    case 'playing':
      return playingEnd;
    case 'revealing':
      return revealEnd;
    case 'showing_result':
      return showingEnd;
    default:
      return null;
  }
}

/**
 * Close-boundary grace when `timing.bettingCloseGraceMs` is unset. Mirrors
 * DEFAULT_BETTING_CLOSE_GRACE_MS in @apx/engine — no game overrides it, so it is
 * one value rather than a per-game column.
 */
const DEFAULT_CLOSE_GRACE_MS = 600;

/**
 * Per-game phase timing, mirroring each module's `defaultConfig.timing`.
 *
 * This WAS a single table (greedyking's 30s/4s/5s) applied to every game. Since
 * `GET …/config/:surface` returns the stored OVERRIDE and never merges module
 * defaults, a game with no timing override rendered greedyking's numbers as if
 * they were its own: the form lied, the live phase-deadline readout below
 * derived from the wrong durations, "Reset to defaults" loaded the wrong values,
 * and one Propose WROTE them — which for baccarat cut betting 40s→30s and the
 * result display 12.5s→5s.
 *
 * Duplicated from the game packages rather than imported, to keep five game
 * modules out of the admin bundle.
 *
 * This table bites hardest when stale: it is not merely displayed, it is what
 * "Reset to defaults" loads and therefore what a subsequent Propose WRITES to a
 * game's config. If a module's `defaultConfig.timing` changes, change it here in
 * the same commit. A game missing here silently falls back to `GENERIC_TIMING`,
 * which is nobody's real timing. `timing-defaults.test.ts` guards the games
 * whose packages admin-ui can import; the rest are still unguarded.
 */
export const TIMING_DEFAULTS: Record<string, TimingOverride> = {
  greedyking: {
    bettingDurationMs: 30_000,
    bettingClosingMs: 2_000,
    playingMs: 4_000,
    revealMs: 1_500,
    showingResultMs: 5_000,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  sicbo: {
    bettingDurationMs: 15_000,
    bettingClosingMs: 2_000,
    playingMs: 2_500,
    revealMs: 1_500,
    showingResultMs: 3_000,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  dragontiger: {
    bettingDurationMs: 15_000,
    bettingClosingMs: 2_000,
    playingMs: 2_000,
    revealMs: 1_500,
    showingResultMs: 3_000,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  singaporelottery: {
    bettingDurationMs: 48_000,
    bettingClosingMs: 2_000,
    playingMs: 2_000,
    revealMs: 1_000,
    showingResultMs: 5_000,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  baccarat: {
    bettingDurationMs: 40_000,
    bettingClosingMs: 2_000,
    playingMs: 2_000,
    revealMs: 1_500,
    showingResultMs: 12_500,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  // The two Dice Rush tables share one phase shape and differ only in the
  // betting window: one minute on the short table, five on the long one.
  shortdicerush: {
    bettingDurationMs: 60_000,
    bettingClosingMs: 2_000,
    playingMs: 3_000,
    revealMs: 1_500,
    showingResultMs: 9_500,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
  longdicerush: {
    bettingDurationMs: 300_000,
    bettingClosingMs: 2_000,
    playingMs: 3_000,
    revealMs: 1_500,
    showingResultMs: 9_500,
    nextRoundDelayMs: 2_000,
    bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
  },
};

/** Fallback for a game with no entry above — the platform's mid-range shape. */
const GENERIC_TIMING: TimingOverride = {
  bettingDurationMs: 30_000,
  bettingClosingMs: 2_000,
  playingMs: 2_000,
  revealMs: 1_500,
  showingResultMs: 5_000,
  nextRoundDelayMs: 2_000,
  bettingCloseGraceMs: DEFAULT_CLOSE_GRACE_MS,
};

/** The module defaults this game actually runs when it has no override. */
export function timingDefaultsFor(gameId: string): TimingOverride {
  return TIMING_DEFAULTS[gameId] ?? GENERIC_TIMING;
}

const FIELD_LABELS: Record<keyof TimingOverride, string> = {
  bettingDurationMs: 'Betting duration (ms)',
  bettingClosingMs: 'Betting closing (ms)',
  playingMs: 'Playing (ms)',
  revealMs: 'Reveal (ms)',
  showingResultMs: 'Showing result (ms)',
  nextRoundDelayMs: 'Next round delay (ms)',
  // Close-boundary grace: accept bets landing this many ms after close (skew +
  // latency absorber). Clamped ≤ betting-closing at enforcement. Default 600.
  bettingCloseGraceMs: 'Close grace (ms)',
};

export async function renderTimingPage(
  root: HTMLElement,
  deps: TimingPageDeps,
): Promise<void> {
  // THIS game's module defaults — what the engine runs when no override exists.
  // Never a shared table: see the note on TIMING_DEFAULTS.
  const defaults = timingDefaultsFor(deps.gameId);
  root.innerHTML = `
    <h1 class="page-title">Phase Timing — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Per-integrator override of the round state-machine timing. Changes require two-admin approval (proposer + a different approver).</p>

    <div class="card">
      <h2>Effective timing</h2>
      <div id="timing-form">
        <div class="form-grid">${(Object.keys(FIELD_LABELS) as Array<keyof TimingOverride>)
          .map(
            (k) => `<label>${FIELD_LABELS[k]}
              <input data-field="${k}" type="number" min="0" step="50" />
            </label>`,
          )
          .join('')}</div>
        <div class="row">
          <button id="timing-save" class="primary">Propose change</button>
          <button id="timing-reset" class="ghost">Reset to defaults</button>
          <span id="timing-msg" class="muted"></span>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Live round status</h2>
      <div id="round-status" class="eta-readout">
        <span class="number" id="round-eta">—</span>
        <div>
          <div><span class="pill" id="round-state-pill">idle</span></div>
          <div class="muted" id="round-id" style="margin-top:6px">no active round</div>
          <div id="round-err" style="margin-top:6px"></div>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="pending-approvals"><p class="muted">Loading…</p></div>
    </div>
  `;

  const inputs = new Map<keyof TimingOverride, HTMLInputElement>();
  for (const k of Object.keys(FIELD_LABELS) as Array<keyof TimingOverride>) {
    const el = root.querySelector<HTMLInputElement>(`input[data-field="${k}"]`);
    if (el) inputs.set(k, el);
  }

  function fillForm(value: TimingOverride): void {
    // Fall back to the module default for keys a pre-existing saved config
    // predates (e.g. bettingCloseGraceMs on configs saved before it existed).
    for (const [k, input] of inputs) input.value = String(value[k] ?? defaults[k] ?? '');
  }

  function readForm(): TimingOverride {
    const out: Record<string, number> = {};
    for (const [k, input] of inputs) out[k] = Number(input.value);
    return out as unknown as TimingOverride;
  }

  // Load current effective surface
  const { value: current } = await deps.client.getSurface(
    deps.integratorId,
    deps.gameId,
    'timing',
  );
  fillForm(current ?? defaults);

  // Timing the ENGINE is actually running, used to derive the live phase
  // deadline. Deliberately not read from the form — the inputs are editable and
  // a half-typed proposal must not shift the live countdown.
  let effectiveTiming: TimingOverride = { ...defaults, ...(current ?? {}) };

  const msgEl = root.querySelector<HTMLElement>('#timing-msg')!;

  root.querySelector<HTMLButtonElement>('#timing-reset')!.addEventListener('click', () => {
    fillForm(defaults);
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#timing-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = timingSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'timing',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent =
        err instanceof AdminApiError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
          ? err.message
          : 'Unknown error';
      msgEl.className = 'error';
    }
  });

  // Pending approvals list (filtered to surface=timing)
  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'timing' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#pending-approvals')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending value</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${escapeHtml(JSON.stringify(a.pendingConfig))}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — config now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'timing');
          fillForm(fresh.value ?? defaults);
          // Approval went live — the engine now ticks against these durations,
          // so the phase countdown must follow.
          effectiveTiming = { ...defaults, ...(fresh.value ?? {}) };
        } catch (err) {
          msgEl.textContent =
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          msgEl.textContent = 'Rejected.';
          await refreshPending();
        } catch (err) {
          msgEl.textContent =
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();

  // Live round status — poll the engine on a fixed cadence and tick the
  // displayed countdown locally between polls. The countdown targets the
  // CURRENT phase's deadline (see `phaseDeadline`), not just `bettingClosesAt`,
  // so playing / revealing / showing_result read as real durations instead of
  // sitting pinned at 0.0s once betting closes.
  const etaEl = root.querySelector<HTMLElement>('#round-eta')!;
  const stateEl = root.querySelector<HTMLElement>('#round-state-pill')!;
  const idEl = root.querySelector<HTMLElement>('#round-id')!;
  const errEl = root.querySelector<HTMLElement>('#round-err')!;
  let active: ActiveRoundView | null = null;
  let inFlight = false;
  let disposed = false;

  function setError(msg: string): void {
    errEl.textContent = msg;
    errEl.className = 'error';
  }
  function clearError(): void {
    errEl.textContent = '';
    errEl.className = '';
  }

  function renderIdle(detail: string): void {
    active = null;
    etaEl.textContent = '—';
    stateEl.textContent = 'idle';
    stateEl.className = 'pill warn';
    idEl.textContent = detail;
  }

  function render(): void {
    if (!active) return;
    // findLatestRound falls back to the most recent FINISHED round when nothing
    // is running, so a non-null response is not proof of a live round.
    if (active.state === 'finished') {
      const last = active.id;
      renderIdle(`no active round · last: ${last}`);
      return;
    }
    stateEl.textContent = active.state;
    stateEl.className = active.state === 'betting_open' ? 'pill live' : 'pill';
    idEl.textContent = active.id;
  }

  async function fetchActive(): Promise<void> {
    // One request at a time: without this the poll can overlap on a slow link
    // and a late response can overwrite fresher state with older state.
    if (inFlight || disposed) return;
    inFlight = true;
    try {
      const res = await fetch(
        `${deps.roundEngineBaseUrl}/v1/rounds/active?integratorId=${encodeURIComponent(
          deps.integratorId,
        )}&gameId=${encodeURIComponent(deps.gameId)}`,
      );
      if (disposed) return;
      if (res.status === 404) {
        renderIdle('/v1/rounds/active not implemented');
        stateEl.textContent = 'no endpoint yet';
        setError('Round engine did not recognise /v1/rounds/active.');
        return;
      }
      if (!res.ok) {
        // 400 INVALID_QUERY (non-UUID integratorId), 5xx, proxy failure. Say so
        // rather than leaving a stale reading on screen indefinitely.
        setError(`Round status unavailable — HTTP ${res.status}.`);
        return;
      }
      // A deployment missing the /round-engine/* edge route serves the SPA's
      // index.html here with a 200; JSON-parsing that throws "Unexpected token
      // '<'". Name the cause instead of failing silently.
      const contentType = res.headers.get('content-type') ?? '';
      if (!contentType.includes('json')) {
        setError('Round status unavailable — proxy returned HTML, not JSON.');
        return;
      }
      const body = (await res.json()) as ActiveRoundView | null;
      clearError();
      if (!body) {
        renderIdle(
          deps.isProviderScope
            ? 'no active round — provider scope has no rounds of its own; pick an integrator'
            : 'no active round',
        );
        return;
      }
      active = body;
      render();
    } catch (err) {
      if (!disposed) {
        setError(
          `Round status unavailable — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    } finally {
      inFlight = false;
    }
  }

  function tick(): void {
    if (!active) return;
    const deadline = phaseDeadline(active, effectiveTiming);
    if (deadline === null) {
      // SETTLING has no timer-driven deadline — it ends when settle completes.
      etaEl.textContent = '—';
      return;
    }
    etaEl.textContent = `${(Math.max(0, deadline - Date.now()) / 1000).toFixed(1)}s`;
  }

  await fetchActive();
  tick();
  const poller = window.setInterval(() => void fetchActive(), ROUND_POLL_MS);
  const ticker = window.setInterval(tick, ROUND_TICK_MS);
  const onVis = (): void => {
    if (document.visibilityState === 'visible') void fetchActive();
  };
  document.addEventListener('visibilitychange', onVis);

  // Cleanup on route change. The tab body is REPLACED by its parent, which
  // fires a mutation on the parent — not on `root` — so this has to watch the
  // document for `root` leaving the tree (same shape as round-ops.ts).
  // Observing `root` itself never fires and leaks an interval per tab visit.
  const cleanupObserver = new MutationObserver(() => {
    if (!document.body.contains(root)) {
      disposed = true;
      window.clearInterval(poller);
      window.clearInterval(ticker);
      document.removeEventListener('visibilitychange', onVis);
      cleanupObserver.disconnect();
    }
  });
  cleanupObserver.observe(document.body, { childList: true, subtree: true });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
